import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MetaNewCampaign = () => {
  const history = useHistory();
  const { token: authToken } = useSelector((state) => state.auth);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    productRetailerId: "",
    productMetaId: "",
    dailyBudget: 10,
    ageMin: 18,
    ageMax: 65,
    locations: "US",
    catalogLandingUrl: "",
    startDate: "",
    endDate: "",
  });

  const headers = () => ({ Authorization: `Bearer ${authToken}` });
  const ev = (e) => (e && e.target ? e.target.value : "");

  useEffect(() => {
    if (!authToken) return;
    axios
      .get("/ad-accounts", { headers: headers() })
      .then((res) => {
        const list = (res.data.data || []).filter((a) => !!a.metaCatalogId);
        setAdAccounts(list);
        if (list.length > 0) setSelectedAccount(list[0]);
      })
      .catch(() => setAdAccounts([]));
  }, [authToken]);

  useEffect(() => {
    if (!selectedAccount?.id || !selectedAccount?.metaCatalogId) return;
    setLoadingProducts(true);
    axios
      .get(`/ad-accounts/${selectedAccount.id}/catalogs/products?catalog_id=${encodeURIComponent(selectedAccount.metaCatalogId)}`, {
        headers: headers(),
      })
      .then((res) => {
        const items = res.data.data || [];
        setCatalogProducts(items);
        if (items.length > 0) {
          const first = items[0];
          setForm((f) => ({
            ...f,
            productRetailerId: first.retailer_id || "",
            productMetaId: first.id || "",
            catalogLandingUrl: first.url || f.catalogLandingUrl,
            name: f.name || `Campaign - ${(first.name || "").slice(0, 40)}`,
          }));
        }
      })
      .catch(() => setCatalogProducts([]))
      .finally(() => setLoadingProducts(false));
  }, [selectedAccount?.id, selectedAccount?.metaCatalogId]);

  const updateProductSelection = (retailerId) => {
    const p = catalogProducts.find((x) => String(x.retailer_id) === String(retailerId));
    setForm((f) => ({
      ...f,
      productRetailerId: retailerId,
      productMetaId: p?.id || "",
      catalogLandingUrl: p?.url || f.catalogLandingUrl,
      name: f.name || (p?.name ? `Campaign - ${p.name.slice(0, 40)}` : f.name),
    }));
  };

  const payload = () => ({
    name: form.name,
    metaCatalogId: selectedAccount.metaCatalogId,
    metaProductRetailerId: form.productRetailerId,
    metaProductId: form.productMetaId || undefined,
    budget: { daily: Number(form.dailyBudget) },
    targetAudience: {
      ageMin: Number(form.ageMin),
      ageMax: Number(form.ageMax),
      locations: form.locations.split(",").map((s) => s.trim()).filter(Boolean),
    },
    catalogLandingUrl: form.catalogLandingUrl || undefined,
    startDate: form.startDate || undefined,
    endDate: form.endDate || undefined,
  });

  const createCampaign = (e) => {
    e.preventDefault();
    if (!selectedAccount?.id || !selectedAccount?.metaCatalogId || !form.productRetailerId || !form.name) {
      toast.error("Please select account/catalog/product and campaign name");
      return;
    }
    setSubmitting(true);
    axios
      .post(`/ad-accounts/${selectedAccount.id}/campaigns`, payload(), { headers: headers() })
      .then(() => {
        toast.success("Campaign created");
        history.push("/seller/social-advertisement");
      })
      .catch((err) => toast.error(err.response?.data?.message || "Campaign creation failed"))
      .finally(() => setSubmitting(false));
  };

  const saveDraft = () => {
    if (!selectedAccount?.id || !selectedAccount?.metaCatalogId || !form.productRetailerId || !form.name) {
      toast.error("Please select account/catalog/product and campaign name");
      return;
    }
    setSavingDraft(true);
    axios
      .post(
        "/campaigns/draft",
        {
          name: form.name,
          adAccountRef: selectedAccount.id,
          metaCatalogId: selectedAccount.metaCatalogId,
          metaProductRetailerId: form.productRetailerId,
          metaProductId: form.productMetaId || undefined,
          budget: { daily: Number(form.dailyBudget) },
          targetAudience: {
            ageMin: Number(form.ageMin),
            ageMax: Number(form.ageMax),
            locations: form.locations.split(",").map((s) => s.trim()).filter(Boolean),
          },
          catalogLandingUrl: form.catalogLandingUrl || undefined,
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
        },
        { headers: headers() }
      )
      .then(() => toast.success("Draft saved"))
      .catch((err) => toast.error(err.response?.data?.message || "Failed to save draft"))
      .finally(() => setSavingDraft(false));
  };

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader><b>Social Advertisement - New Campaign</b></CardHeader>
            <CardBody>
              <Form onSubmit={createCampaign}>
                <FormGroup>
                  <Label>Ad account (catalog connected)</Label>
                  <Input
                    type="select"
                    value={selectedAccount?.id || ""}
                    onChange={(e) => {
                      const id = ev(e);
                      setSelectedAccount(adAccounts.find((a) => a.id === id) || null);
                    }}
                  >
                    {adAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.accountName || a.accountId} / Catalog: {a.metaCatalogId}
                      </option>
                    ))}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label>Product (from selected Meta catalog)</Label>
                  {loadingProducts ? (
                    <div><i className="fas fa-spinner fa-pulse" aria-hidden /> Loading catalog products...</div>
                  ) : (
                    <Input
                      type="select"
                      value={form.productRetailerId}
                      onChange={(e) => updateProductSelection(ev(e))}
                    >
                      <option value="">-- Select product --</option>
                      {catalogProducts.map((p) => (
                        <option key={p.id} value={p.retailer_id || ""}>
                          {(p.name || "Untitled")} / retailer_id: {p.retailer_id || "-"}
                        </option>
                      ))}
                    </Input>
                  )}
                </FormGroup>

                <FormGroup>
                  <Label>Campaign name</Label>
                  <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: ev(e) }))} required />
                </FormGroup>

                <Row form>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Daily budget (USD)</Label>
                      <Input type="number" min={1} step={1} value={form.dailyBudget} onChange={(e) => setForm((f) => ({ ...f, dailyBudget: ev(e) }))} />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Age min</Label>
                      <Input type="number" min={18} max={65} value={form.ageMin} onChange={(e) => setForm((f) => ({ ...f, ageMin: ev(e) }))} />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Age max</Label>
                      <Input type="number" min={18} max={65} value={form.ageMax} onChange={(e) => setForm((f) => ({ ...f, ageMax: ev(e) }))} />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>Locations (comma-separated country codes)</Label>
                  <Input value={form.locations} onChange={(e) => setForm((f) => ({ ...f, locations: ev(e) }))} />
                </FormGroup>

                <Row form>
                  <Col md={6}>
                    <FormGroup>
                      <Label>Start date (optional)</Label>
                      <Input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: ev(e) }))} />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label>End date (optional)</Label>
                      <Input type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: ev(e) }))} />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>Landing URL (optional)</Label>
                  <Input type="url" value={form.catalogLandingUrl} onChange={(e) => setForm((f) => ({ ...f, catalogLandingUrl: ev(e) }))} />
                </FormGroup>

                <Button type="submit" color="primary" disabled={submitting}>
                  {submitting ? "Creating..." : "Create campaign"}
                </Button>
                <Button type="button" color="warning" className="ml-2" onClick={saveDraft} disabled={savingDraft}>
                  {savingDraft ? "Saving..." : "Save draft"}
                </Button>
                <Button type="button" color="secondary" className="ml-2" onClick={() => history.push("/seller/social-advertisement")}>
                  Back
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaNewCampaign;

